import React, { useEffect, useState } from "react";
import { Plus, Trash2, Quote as QuoteIcon, Camera, DollarSign, PiggyBank, ChevronRight } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Inp, Sec } from "../components";
import { notify, confirmDialog } from "../notify";
import { EventGallery } from "../components/EventGallery";
import { DeductionsManager } from "../components/DeductionsManager";
import { AdditionsManager } from "../components/AdditionsManager";
import { usePermissions } from "../usePermissions";

// HR Corner — a landing spot for HR-managed content that anyone in the
// company should be able to view or that HR wants to publish. Tabs are
// gated per role: Quotes is HR/admin-only, Event Gallery opens up to
// centre_head + anyone granted HR Corner → Media Hub in Settings → Permissions (Social Media Team).

const QuotesSection = () => {
  const [rows, setRows] = useState(null);
  const [text, setText] = useState('');
  const [author, setAuthor] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get('/api/quotes').then(d => setRows(d || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const t = text.trim();
    if (!t) { notify('Enter the quote text', { title: 'Error', variant: 'error' }); return; }
    setAdding(true);
    try {
      await api.post('/api/quotes', { text: t, author: author.trim() || null });
      setText(''); setAuthor('');
      notify('Quote added', { title: 'Saved', variant: 'success' });
      load();
    } catch (e) { notify(e.message || 'Failed to add quote', { title: 'Error', variant: 'error' }); }
    finally { setAdding(false); }
  };

  const toggle = async (q) => {
    setBusyId(q.id);
    try {
      await api.put('/api/quotes/' + q.id, { active: !q.active });
      notify(q.active ? 'Quote deactivated' : 'Quote activated', { title: 'Saved', variant: 'success' });
      load();
    } catch (e) { notify(e.message || 'Failed to update quote', { title: 'Error', variant: 'error' }); }
    finally { setBusyId(null); }
  };

  const del = async (q) => {
    if (!(await confirmDialog(`Delete this quote?\n\n"${q.text.slice(0, 100)}..."`))) return;
    setBusyId(q.id);
    try {
      await api.del('/api/quotes/' + q.id);
      notify('Quote deleted', { title: 'Saved', variant: 'success' });
      load();
    } catch (e) { notify(e.message || 'Failed to delete quote', { title: 'Error', variant: 'error' }); }
    finally { setBusyId(null); }
  };

  return (
    <Cd>
      <Sec icon={QuoteIcon} title="Quotes of the Day" />
      <div className="text-[14px] text-t3 mb-3 -mt-1">
        One quote is shown on the dashboard each day, rotated deterministically so the whole org sees the same one. Deactivate to retire without deleting.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-[6px] mb-3">
        <Inp value={text} onChange={setText} placeholder="Quote text (up to 500 chars)…" />
        <Inp value={author} onChange={setAuthor} placeholder="Author (optional)" />
        <Bt icon={Plus} disabled={adding} onClick={add}>{adding ? 'Adding…' : 'Add'}</Bt>
      </div>
      {rows === null ? (
        <div className="text-[14px] text-t3 p-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[14px] text-t3 p-4">No quotes yet — add one above.</div>
      ) : (
        <div className="rounded-lg border border-b0 overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Quote</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Author</th>
              <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Status</th>
              <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {rows.map(q => (
                <tr key={q.id} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-[6px] text-[14px] text-t1 italic max-w-[520px]">&ldquo;{q.text}&rdquo;</td>
                  <td className="px-2 py-[6px] text-[14px] text-t2">{q.author || '—'}</td>
                  <td className="px-2 py-[6px] text-center">
                    {q.active ? <Bg c={K.g} sm dot>Active</Bg> : <Bg c={K.t3} sm>Inactive</Bg>}
                  </td>
                  <td className="px-2 py-[6px] text-right whitespace-nowrap">
                    <Bt v="ghost" s="xs" disabled={busyId === q.id} onClick={() => toggle(q)}>{q.active ? 'Deactivate' : 'Activate'}</Bt>
                    <Bt v="ghost" s="xs" icon={Trash2} disabled={busyId === q.id} onClick={() => del(q)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cd>
  );
};

export const HrCornerPage = ({ role, myProfile, canMediaHub, canDeleteEvents = false, companies = [], offices = [], employees = [], hashQuery }) => {
  const { can: pcan } = usePermissions(true);
  const canManageQuotes = pcan('hrcorner', 'quotes', 'manage_quotes', 'edit');
  const canSeeGallery = !!canMediaHub;
  // Deductions/Additions tabs: admin / hr_head / centre_hr can create +
  // approve (centre_hr scoped to their own centre); centre_head sees rows
  // in their scope (view-only bulk approve path stays with HR/admin/centre_hr).
  // Accounts also gets a view lens since they reconcile payroll.
  const canSeeDeductions = pcan('hrcorner', 'deductions', null, 'view');
  const canSeeAdditions = pcan('hrcorner', 'additions', 'manage_additions', 'view');

  // Default to whichever tab this user actually has access to.
  const defaultTab = canManageQuotes ? 'quotes'
                   : canSeeDeductions ? 'deductions'
                   : canSeeAdditions ? 'additions'
                   : canSeeGallery ? 'gallery'
                   : 'quotes';
  // A notification's #hrcorner?tab=deductions&id=… link should land on
  // that tab directly rather than whatever this role defaults to.
  const [tab, setTab] = useState(hashQuery?.tab || defaultTab);
  useEffect(() => { if (hashQuery?.tab) setTab(hashQuery.tab); }, [hashQuery?.tab]);
  // Consumed once by DeductionsManager/AdditionsManager below to open that
  // specific record's detail drawer on arrival, same id-deep-link pattern
  // used for employee profiles.
  const deepLinkId = hashQuery?.id || null;

  const tabCls = (k) => `px-1 py-3 mr-8 text-[14px] cursor-pointer border-b-2 flex items-center gap-2 ${tab === k ? "font-bold text-p border-p" : "font-semibold text-t2 border-transparent hover:text-t0 hover:border-b1"}`;

  if (!canManageQuotes && !canSeeGallery && !canSeeDeductions && !canSeeAdditions) {
    return (
      <div>
        <h1 className="m-0 mb-[14px] text-2xl font-extrabold text-t0">HR Corner</h1>
        <Cd>
          <div className="text-center p-[30px] text-t3 text-[14px]">You don't have access to any section here yet. Ask HR to enable Media Hub access if you're on the Social Media team.</div>
        </Cd>
      </div>
    );
  }

  const crumbLabel = tab === 'quotes' ? 'Quotes of the Day' : tab === 'deductions' ? 'Deductions Ledger' : tab === 'additions' ? 'Additions Ledger' : 'Event Gallery';

  return (
    <div>
      <div className="mb-[14px]">
        <div className="flex items-center gap-1.5 text-[12px] text-t3 font-medium mb-1">
          <span>Resources</span>
          <ChevronRight size={12} className="text-t3" />
          <span>HR Corner</span>
          <ChevronRight size={12} className="text-t3" />
          <span className="text-p font-semibold">{crumbLabel}</span>
        </div>
        <h1 className="m-0 text-2xl font-bold text-t0 tracking-tight">HR Corner</h1>
      </div>
      <div className="flex gap-0 border-b border-b0 mb-4 flex-wrap whitespace-nowrap">
        {canManageQuotes && (
          <button className={tabCls('quotes')} onClick={() => setTab('quotes')}>
            <QuoteIcon size={14} className="text-t3" />Quotes of the Day
          </button>
        )}
        {canSeeDeductions && (
          <button className={tabCls('deductions')} onClick={() => setTab('deductions')}>
            <DollarSign size={14} className="text-t3" />Deductions
          </button>
        )}
        {canSeeAdditions && (
          <button className={tabCls('additions')} onClick={() => setTab('additions')}>
            <PiggyBank size={14} className="text-t3" />Additions
          </button>
        )}
        {canSeeGallery && (
          <button className={tabCls('gallery')} onClick={() => setTab('gallery')}>
            <Camera size={14} className="text-t3" />Event Gallery
          </button>
        )}
      </div>
      {tab === 'quotes' && canManageQuotes && <QuotesSection />}
      {tab === 'deductions' && canSeeDeductions && (
        <DeductionsManager role={role} companies={companies} offices={offices} employees={employees} initialOpenId={deepLinkId} />
      )}
      {tab === 'additions' && canSeeAdditions && (
        <AdditionsManager role={role} companies={companies} offices={offices} employees={employees} initialOpenId={deepLinkId} />
      )}
      {tab === 'gallery' && canSeeGallery && (
        <EventGallery role={role} myProfile={myProfile} companies={companies} offices={offices} canDeleteEvents={canDeleteEvents} />
      )}
    </div>
  );
};
